import { useState } from 'react';
import { Link, Navigate, NavLink, Outlet, useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import clsx from 'clsx';
import {
  AtSign,
  Bell,
  BookOpen,
  ClipboardList,
  FileSignature,
  FolderClosed,
  House,
  Inbox,
  Lock,
  LogOut,
  Menu,
  Moon,
  MonitorSmartphone,
  Plus,
  Settings,
  Share2,
  Sun,
  Trash2,
  Users,
} from 'lucide-react';

import NewWorkspaceModal from '../features/tenancy/NewWorkspaceModal';
import WorkspaceAvatar from '../features/tenancy/WorkspaceAvatar';
import WorkspaceSettingsModal from '../features/tenancy/WorkspaceSettingsModal';
import ConfirmDialog from './ui/ConfirmDialog';
import Select from './ui/Select';
import HeaderSearch from './HeaderSearch';
import ResponsiveSidebar from './ResponsiveSidebar';
import VerificationBanner from '../features/auth/VerificationBanner';
import ImpersonationBanner from './ImpersonationBanner';
import MaintenanceBanner from './MaintenanceBanner';
import { billingKeys, getBillingSubscription } from '../features/billing/billingApi';
import { useAuthStore } from '../store/authStore';
import { useThemeStore } from '../store/themeStore';
import { useCurrentUser } from '../hooks/useCurrentUser';
import { useAvatarUrl } from '../hooks/useAvatarUrl';
import { useTenant } from '../hooks/useTenant';
import { ACTIONS } from '../features/tenancy/actions';
import { logoutRequest } from '../features/auth/authApi';
import { commentKeys, myMentions } from '../features/comments/commentsApi';
import { notificationKeys, unreadCount } from '../features/notifications/notificationsApi';
import { formatBytes, quotaPercent } from '../utils/format';
import Avatar from './ui/Avatar';
import Spinner from './ui/Spinner';
import BrandLogo from './BrandLogo';

// Contextual sidebar — full navigation for the active workspace.
const NAV = [
  { to: '/files', label: 'All files', icon: FolderClosed, end: true },
  { to: '/shares', label: 'Shared', icon: Share2, requiresAction: ACTIONS.SHARE_CREATE },
  { to: '/shared-with-me', label: 'Shared with me', icon: Inbox },
  { to: '/file-requests', label: 'File requests', icon: Inbox, requiresAction: ACTIONS.FILE_REQUEST_CREATE },
  { to: '/messages/sent', label: 'Secure Drop', icon: Lock, requiresAction: ACTIONS.SECRET_READ },
  { to: '/signatures', label: 'Signatures', icon: FileSignature, requires: 'sendSignatures' },
  { to: '/forms', label: 'Forms', icon: ClipboardList, requires: 'manageForms' },
  { to: '/mentions', label: 'Mentions', icon: AtSign, badge: 'mentions' },
  { to: '/members', label: 'Members', icon: Users },
  { to: '/sessions', label: 'Devices', icon: MonitorSmartphone },
  { to: '/trash', label: 'Deleted files', icon: Trash2 },
];

export default function AppLayout() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const refreshToken = useAuthStore((s) => s.refreshToken);
  const clear = useAuthStore((s) => s.clear);
  const isImpersonating = useAuthStore((s) => Boolean(s.impersonation));
  const { data: user } = useCurrentUser();
  const avatarUrl = useAvatarUrl();
  const {
    ready,
    orgs,
    organization,
    activeOrgId,
    can,
    workspaces,
    workspace,
    switchWorkspace,
    switchOrg,
    allows,
  } = useTenant();
  const [newWorkspaceOpen, setNewWorkspaceOpen] = useState(false);
  const [workspaceSettingsOpen, setWorkspaceSettingsOpen] = useState(false);
  const [signOutConfirmOpen, setSignOutConfirmOpen] = useState(false);
  const [profileMenuOpen, setProfileMenuOpen] = useState(false);
  const [signingOut, setSigningOut] = useState(false);
  const [navOpen, setNavOpen] = useState(false);
  const isWorkspaceOwner = Boolean(workspace?.ownerId && workspace.ownerId === user?.id);

  // Mention inbox badge — cross-workspace, light polling so the count stays fresh.
  const { data: unseenMentions = [] } = useQuery({
    queryKey: commentKeys.myMentions(true),
    queryFn: () => myMentions(true),
    refetchInterval: 60_000,
  });
  const mentionCount = unseenMentions.length;

  const theme = useThemeStore((s) => s.theme);
  const toggleTheme = useThemeStore((s) => s.toggleTheme);

  // Notification bell — unread badge, light polling.
  const { data: unread = 0 } = useQuery({
    queryKey: notificationKeys.unreadCount(),
    queryFn: unreadCount,
    refetchInterval: 60_000,
  });

  const handleLogout = async () => {
    setSigningOut(true);
    try {
      await logoutRequest(refreshToken);
    } finally {
      clear();
      queryClient.clear();
      navigate('/login', { replace: true });
    }
  };

  const handleNewFolder = () => {
    navigate('/files', { state: { openNewFolder: true } });
  };

  // Card-before-entry gate: a pending (incomplete) signup subscription sends
  // the owner to the billing setup page. Old orgs have no such row.
  const { data: billing } = useQuery({
    queryKey: billingKeys.subscription,
    queryFn: getBillingSubscription,
    staleTime: 60_000,
    retry: false,
  });
  const billingStatus = billing?.subscription?.status ?? null;
  // Billing is org-owner-only. Invited/assignment-scoped members own no org, so
  // the endpoint returns canManageBilling=false — hide every billing entry point
  // and never bounce them to the setup page they could not complete.
  const canManageBilling = billing?.canManageBilling ?? false;

  const used = organization?.storageUsedBytes ?? 0;
  const quota = organization?.storageQuotaBytes ?? 0;
  const percent = quotaPercent(used, quota);
  const displayName = user?.displayName ?? user?.email ?? 'You';
  const badgeFor = (key) =>
    key === 'mentions' ? mentionCount : key === 'unread' ? unread : 0;

  // `incomplete` = signup chose a plan but never finished the card step.
  // `subscription === null` (with the payload loaded) = the org has no
  // subscription at all — e.g. a Google signup that skipped the plan step;
  // the setup page shows a plan picker for that case.
  // Exempt: platform staff don't subscribe, and an impersonation session
  // must never be trapped in the target owner's billing setup.
  // `user &&` waits for /users/me so a super admin isn't bounced before their
  // role is known (the two queries race on first paint).
  const billingExempt = user?.role === 'super_admin' || isImpersonating;
  if (
    user &&
    !billingExempt &&
    canManageBilling &&
    (billingStatus === 'incomplete' || billing?.subscription === null)
  ) {
    return <Navigate to="/billing/setup" replace />;
  }

  return (
    <div className="flex h-full flex-col bg-slate-50 text-slate-700">
      <ImpersonationBanner />
      <MaintenanceBanner />
      <div className="flex min-h-0 flex-1">
      {/* ---- Sidebar (full navigation for the active workspace; drawer on mobile) ---- */}
      <ResponsiveSidebar
        open={navOpen}
        onClose={() => setNavOpen(false)}
        className="w-[260px] shrink-0 border-r border-slate-200 bg-slate-50"
      >
        <div className="flex items-center gap-2 px-5 pb-3 pt-5">
          <BrandLogo className="h-8 w-auto max-w-full" />
        </div>

        {/* Organization switcher (only when the user belongs to more than one) */}
        {orgs.length > 1 && (
          <div className="px-3 pb-2">
            <Select
              size="sm"
              className="w-full"
              ariaLabel="Organization"
              value={activeOrgId}
              onChange={(id) => switchOrg(id)}
              options={orgs.map((o) => ({ value: o.id, label: o.name, description: o.role }))}
            />
          </div>
        )}

        {/* Workspace switcher */}
        <div className="px-3 pb-3">
          <div className="flex items-center gap-1.5">
            {/* The trigger doubles as the workspace header: a readable logo tile that
                keeps the workspace's brand on screen, not just inside the open list. */}
            <Select
              size="lg"
              className="w-full min-w-0 flex-1"
              ariaLabel="Workspace"
              value={workspace?.id ?? null}
              onChange={(id) => switchWorkspace(id)}
              disabled={!workspaces.length}
              options={workspaces.map((w) => ({
                value: w.id,
                label: `${w.name}${w.type === 'personal' ? ' (Personal)' : ''}`,
                icon: <WorkspaceAvatar orgId={activeOrgId} workspace={w} className="h-6 w-6" />,
                workspace: w,
              }))}
              renderValue={(option) => (
                <>
                  <WorkspaceAvatar
                    orgId={activeOrgId}
                    workspace={option.workspace}
                    className="h-9 w-9 rounded-lg text-sm"
                  />
                  <span className="min-w-0 flex-1">
                    <span className="block truncate font-medium text-slate-800">
                      {option.workspace.name}
                    </span>
                    <span className="block truncate text-xs text-slate-400">
                      {option.workspace.type === 'personal' ? 'Personal' : 'Shared workspace'}
                    </span>
                  </span>
                </>
              )}
            />
            {isWorkspaceOwner && (
              <button
                onClick={() => setWorkspaceSettingsOpen(true)}
                title="Workspace settings"
                aria-label="Workspace settings"
                className="flex h-14 w-11 shrink-0 items-center justify-center rounded-lg border border-slate-200 bg-slate-100 text-slate-500 transition-colors hover:bg-slate-200 hover:text-slate-800"
              >
                <Settings className="h-4 w-4" />
              </button>
            )}
          </div>
          {can.createWorkspace && (
            <button
              onClick={() => setNewWorkspaceOpen(true)}
              className="mt-1.5 flex w-full items-center gap-1 rounded-md px-2 py-1 text-xs font-medium text-brand-600 hover:bg-slate-100"
            >
              <Plus className="h-3.5 w-3.5" />
              New workspace
            </button>
          )}
        </div>

        <nav className="flex-1 space-y-0.5 overflow-y-auto px-3 py-1">
          {NAV.filter(
            (item) =>
              (!item.requires || can[item.requires]) &&
              (!item.requiresAction || allows(item.requiresAction)),
          ).map(
            ({ to, label, icon: Icon, end, badge }) => (
              <NavLink
                key={to}
                to={to}
                end={end}
                className={({ isActive }) =>
                  clsx(
                    'flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors',
                    isActive
                      ? 'bg-slate-200/70 text-slate-900'
                      : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900',
                  )
                }
              >
                <Icon className="h-[18px] w-[18px] shrink-0" />
                <span className="flex-1 truncate">{label}</span>
                {badge && badgeFor(badge) > 0 && (
                  <span className="inline-flex h-5 min-w-[1.25rem] items-center justify-center rounded-full bg-brand-600 px-1.5 text-xs font-semibold text-white">
                    {badgeFor(badge)}
                  </span>
                )}
              </NavLink>
            ),
          )}
        </nav>

        {/* Storage / Get-started card. Only the org owner can reach billing. */}
        {(() => {
          const cardInner = (
            <>
              <div className="mb-1.5 flex items-center justify-between text-xs">
                <span className="font-medium text-slate-600">
                  {organization?.name ? `${organization.name}` : 'Storage'}
                </span>
                <span className="text-slate-400">{percent}%</span>
              </div>
              <div className="h-1.5 w-full overflow-hidden rounded-full bg-slate-300">
                <div
                  className={clsx(
                    'h-full rounded-full transition-all',
                    percent > 90 ? 'bg-[#fa551e]' : 'bg-brand-600',
                  )}
                  style={{ width: `${Math.min(100, percent)}%` }}
                />
              </div>
              <p className="mt-1.5 text-[11px] text-slate-400">
                {formatBytes(used)} of {formatBytes(quota)} used
              </p>
            </>
          );
          return (
            <div className="m-3 rounded-xl border border-slate-200 bg-slate-100 p-3">
              {cardInner}
              {canManageBilling && (
                <Link
                  to="/billing"
                  className="mt-2 block w-fit text-[11px] font-medium text-brand-600 hover:text-brand-700 hover:underline"
                >
                  Manage subscription
                </Link>
              )}
            </div>
          );
        })()}

        <button
          onClick={() => setSignOutConfirmOpen(true)}
          className="mx-3 mb-3 flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium text-slate-600 transition-colors hover:bg-slate-100 hover:text-slate-900"
        >
          <LogOut className="h-[18px] w-[18px] shrink-0" />
          Sign out
        </button>
      </ResponsiveSidebar>

      {/* ---- Main column ---- */}
      <div className="flex min-w-0 flex-1 flex-col overflow-hidden">
        {/* Top toolbar */}
        <header className="flex h-[68px] shrink-0 items-center gap-3 border-b border-slate-200 bg-slate-50 px-4">
          <button
            type="button"
            onClick={() => setNavOpen(true)}
            aria-label="Open menu"
            aria-expanded={navOpen}
            className="rounded-full p-2 text-slate-500 transition-colors hover:bg-slate-100 hover:text-slate-800 md:hidden"
          >
            <Menu className="h-5 w-5" />
          </button>

          {allows(ACTIONS.FOLDER_MANAGE) && (
            <button
              type="button"
              onClick={handleNewFolder}
              className="hidden items-center gap-1.5 rounded-full bg-slate-900 px-4 py-2 text-sm font-semibold text-slate-50 transition-colors hover:bg-slate-800 sm:inline-flex"
            >
              <Plus className="h-4 w-4" />
              New
            </button>
          )}

          <HeaderSearch />

          {can.invite && (
            <Link
              to="/members"
              className="hidden items-center gap-1.5 rounded-lg border border-slate-200 bg-slate-100 px-3 py-2 text-sm font-medium text-slate-700 transition-colors hover:bg-slate-200 md:inline-flex"
            >
              <Users className="h-4 w-4" />
              Invite members
            </Link>
          )}

          <button
            onClick={toggleTheme}
            title={theme === 'dark' ? 'Switch to light theme' : 'Switch to dark theme'}
            aria-label={theme === 'dark' ? 'Switch to light theme' : 'Switch to dark theme'}
            className="rounded-full p-2 text-slate-500 transition-colors hover:bg-slate-100 hover:text-slate-800"
          >
            {theme === 'dark' ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}
          </button>

          <Link
            to="/notifications"
            title="Notifications"
            aria-label="Notifications"
            className="relative rounded-full p-2 text-slate-500 transition-colors hover:bg-slate-100 hover:text-slate-800"
          >
            <Bell className="h-5 w-5" />
            {unread > 0 && (
              <span className="absolute right-0.5 top-0.5 inline-flex h-4 min-w-[1rem] items-center justify-center rounded-full bg-[#fa551e] px-1 text-[10px] font-semibold text-white">
                {unread > 9 ? '9+' : unread}
              </span>
            )}
          </Link>

          <div className="relative shrink-0">
            <button
              type="button"
              title={displayName}
              aria-label="Open profile menu"
              aria-expanded={profileMenuOpen}
              onClick={() => setProfileMenuOpen((open) => !open)}
            >
              <Avatar
                name={displayName}
                src={avatarUrl}
                className="h-9 w-9 bg-[#9b0032] text-xs font-semibold text-white"
              />
            </button>
            {profileMenuOpen && (
              <>
                <button
                  type="button"
                  aria-label="Close profile menu"
                  className="fixed inset-0 z-30 cursor-default"
                  onClick={() => setProfileMenuOpen(false)}
                />
                <div className="absolute right-0 z-40 mt-2 w-48 rounded-xl border border-slate-200 bg-slate-100 p-1.5 shadow-lg animate-scale-in">
                  <button
                    type="button"
                    onClick={() => {
                      setProfileMenuOpen(false);
                      navigate('/profile');
                    }}
                    className="flex w-full items-center gap-2 rounded-md px-2.5 py-2 text-sm text-slate-700 hover:bg-slate-200"
                  >
                    <Settings className="h-4 w-4" /> Profile settings
                  </button>
                  <a
                    href="/api-docs"
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={() => setProfileMenuOpen(false)}
                    className="flex w-full items-center gap-2 rounded-md px-2.5 py-2 text-sm text-slate-700 hover:bg-slate-200"
                  >
                    <BookOpen className="h-4 w-4" /> API docs
                  </a>
                  <button
                    type="button"
                    onClick={() => {
                      setProfileMenuOpen(false);
                      setSignOutConfirmOpen(true);
                    }}
                    className="flex w-full items-center gap-2 rounded-md px-2.5 py-2 text-sm text-red-600 hover:bg-red-50"
                  >
                    <LogOut className="h-4 w-4" /> Sign out
                  </button>
                </div>
              </>
            )}
          </div>
        </header>

        <main className="scrollbar-thin flex-1 overflow-auto px-4 py-5 md:px-6">
          {billingStatus === 'past_due' && canManageBilling && (
            <div className="mb-4 flex items-center gap-3 rounded-lg border border-amber-200 bg-amber-50 px-4 py-2.5 text-sm text-amber-800">
              <span className="flex-1">
                Your last payment failed — please update your card to keep your subscription
                active.
              </span>
              <Link to="/billing" className="font-medium text-amber-900 hover:underline">
                Manage subscription
              </Link>
            </div>
          )}
          <VerificationBanner />
          {ready ? (
            <Outlet />
          ) : (
            <div className="flex h-full items-center justify-center">
              <Spinner />
            </div>
          )}
        </main>
      </div>

      <NewWorkspaceModal
        open={newWorkspaceOpen}
        onClose={() => setNewWorkspaceOpen(false)}
        orgId={activeOrgId}
        onCreated={(ws) => switchWorkspace(ws.id)}
      />

      <WorkspaceSettingsModal
        open={workspaceSettingsOpen}
        onClose={() => setWorkspaceSettingsOpen(false)}
        orgId={activeOrgId}
        workspace={workspace}
        onDeleted={() => {
          const personal = workspaces.find((w) => w.type === 'personal' && w.id !== workspace?.id);
          if (personal) switchWorkspace(personal.id);
        }}
      />

      <ConfirmDialog
        open={signOutConfirmOpen}
        onClose={() => setSignOutConfirmOpen(false)}
        onConfirm={handleLogout}
        loading={signingOut}
        title="Sign out?"
        description="You'll need to log in again to access your files."
        confirmLabel="Sign out"
      />
      </div>
    </div>
  );
}
